<template>
  <div ref="rootEl" class="ai-chat-markdown-root"></div>
</template>

<script setup>
  import { nextTick, onMounted, ref, watch } from 'vue'
  import { renderMarkdownInto } from '@/components/plugins/KnowledgeBase/services/MarkdownRendererBridge.js'
  import { getMarkdownPostProcessors } from '@/components/plugins/KnowledgeBase/services/PluginAPI.js'
  import { registerCalloutsPlugin } from '@/components/plugins/KnowledgeBase/plugins/callouts/index.js'
  import { registerQuasarTablesPlugin } from '@/components/plugins/KnowledgeBase/plugins/quasarTables/index.js'
  import { registerQuasarTaskListsPlugin } from '@/components/plugins/KnowledgeBase/plugins/quasarTaskLists/index.js'
  import { registerQuasarCalloutsPlugin } from '@/components/plugins/KnowledgeBase/plugins/quasarCallouts/index.js'
  import { registerQuasarBlockquotePlugin } from '@/components/plugins/KnowledgeBase/plugins/quasarBlockquote/index.js'
  import { registerQuasarImagesPlugin } from '@/components/plugins/KnowledgeBase/plugins/quasarImages/index.js'
  import { registerQuasarCodePlugin } from '@/components/plugins/KnowledgeBase/plugins/quasarCode/index.js'
  import { registerQuasarSeparatorPlugin } from '@/components/plugins/KnowledgeBase/plugins/quasarSeparator/index.js'

  let processorsBootstrapDone = false

  const props = defineProps({
    content: { type: String, default: '' },
    docId: { type: String, default: '' }
  })

  const rootEl = ref(null)
  let renderInFlight = false
  let rerenderRequested = false

  function ensureMarkdownProcessorsReady() {
    if (processorsBootstrapDone) return
    processorsBootstrapDone = true
    if (getMarkdownPostProcessors().length > 0) return
    try {
      registerCalloutsPlugin()
      registerQuasarTablesPlugin()
      registerQuasarTaskListsPlugin()
      registerQuasarCalloutsPlugin()
      registerQuasarBlockquotePlugin()
      registerQuasarImagesPlugin()
      registerQuasarCodePlugin()
      registerQuasarSeparatorPlugin()
    } catch {
      // Keep chat functional even if optional KB processors fail to register.
    }
  }

  function applyTableFallback(root) {
    if (!root) return
    const tables = Array.from(root.querySelectorAll('table'))
    for (const table of tables) {
      if (!table || table.closest('.kb-qmt')) continue
      table.classList.add('kb-qmt-table')
      const wrapper = document.createElement('div')
      wrapper.className = 'kb-qmt'
      const parent = table.parentElement
      if (!parent) continue
      parent.replaceChild(wrapper, table)
      wrapper.appendChild(table)
    }
  }

  const render = async () => {
    rerenderRequested = true
    if (renderInFlight) return
    renderInFlight = true
    try {
      while (rerenderRequested) {
        rerenderRequested = false
        ensureMarkdownProcessorsReady()
        await nextTick()
        if (!rootEl.value) continue
        await renderMarkdownInto(rootEl.value, String(props.content || ''), {
          docId: props.docId || undefined,
          context: {
            viewMode: 'public',
            canEdit: false,
            isPublicView: true,
            noteId: props.docId || undefined
          }
        })
        applyTableFallback(rootEl.value)
      }
    } finally {
      renderInFlight = false
    }
  }

  onMounted(render)

  watch(
    () => props.content,
    () => {
      render()
    }
  )

  watch(
    () => props.docId,
    () => {
      render()
    }
  )
</script>
